import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { labeledArrow, annotationRails } from '../kits/cardKit';
import { inchesToUnits } from '../kits/dotKit';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ToolRecipe } from '../types';

/**
 * Chemistry reaction arrow (CATALOG, cardKit.labeledArrow + annotationRails).
 * A horizontal arrow sized in PHYSICAL INCHES (dotKit.inchesToUnits — 72
 * workspace units per inch), with condition rails riding just above and below
 * the shaft ("catalyst" over, "heat" under, etc.).
 *
 * REVERSIBLE: the kit's doubleHead adds a second arrowhead at the tail, giving
 * a double-headed shaft for equilibria. NOTE: a true equilibrium mark is a
 * pair of offset HALF arrows (⇌); cardKit draws full arrowheads on both ends,
 * so this is the double-headed approximation (◄——►), not stacked half-arrows —
 * flagged as a kit limitation.
 *
 * The above/below rails carry data.tableCell = {kind:'param', key} (assemble.ts)
 * so each is double-click editable in place. A blank rail emits nothing (and
 * has no text child to edit — re-add it from the panel).
 *
 * Emits Line (shaft) + Path (arrowheads) + FabricText (conditions) only,
 * grouped and tagged {tool,version,params} — plain fabric JSON, vector at any
 * export dpi.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern).
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty condition is MEANINGFUL (= no rail on that side). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

const INK = '#1e293b';
const DEFAULT_FONT = 'Arial';

const reactionArrow: ToolRecipe = {
  id: 'reaction-arrow',
  domain: 'science',
  tags: ['chemistry', 'reaction', 'equation', 'arrow', 'equilibrium', 'notation'],
  name: 'Reaction arrow',
  category: 'science',
  version: 1,
  description:
    'A chemistry reaction arrow with conditions above and below. Turn on "Reversible" for an equilibrium arrow. Length is set in inches.',
  // lengthIn is a physical promise (72 units = 1 inch) — no preview-scaling
  // on insert (see insertAtTrueScale in types.ts).
  insertAtTrueScale: true,
  defaults: {
    above: 'catalyst',
    below: 'heat',
    reversible: false,
    lengthIn: 2,
    color: INK,
    fontFamily: DEFAULT_FONT,
  },
  schema: [
    { key: 'above', label: 'Above the arrow', type: 'text', help: 'Conditions over the arrow. Blank = none.' },
    { key: 'below', label: 'Below the arrow', type: 'text', help: 'Conditions under the arrow. Blank = none.' },
    {
      key: 'reversible',
      label: 'Reversible (equilibrium)',
      type: 'boolean',
      help: 'Adds a second arrowhead for a reversible reaction.',
    },
    {
      key: 'lengthIn',
      label: 'Length (inches)',
      type: 'number',
      min: 0.5,
      max: 12,
      step: 0.5,
      section: 'Style',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
  ],
  async generate(params) {
    const above = rawStr(params.above, 'catalyst');
    const below = rawStr(params.below, 'heat');
    const reversible = bool(params.reversible, false);
    const lengthIn = clamp(num(params.lengthIn, 2), 0.5, 12);
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);

    const length = inchesToUnits(lengthIn);
    const strokeWidth = clamp(length * 0.03, 3, 40);
    const fontSize = clamp(length * 0.14, 12, 200);

    // Shaft along y = 0; doubleHead adds the tail arrowhead when reversible.
    const arrow = labeledArrow({
      from: { x: 0, y: 0 },
      to: { x: length, y: 0 },
      color,
      strokeWidth,
      doubleHead: reversible,
    });

    // Rails clear the shaft thickness (and, when reversible, the tail head).
    const rails = annotationRails({
      x1: 0,
      x2: length,
      y: 0,
      above: above ? { text: above, param: 'above' } : undefined,
      below: below ? { text: below, param: 'below' } : undefined,
      fontSize,
      fontFamily: family,
      color,
      gap: Math.max(fontSize * 0.35, strokeWidth + 4),
    });

    const group = new Group(assembleSpecs([...arrow.specs, ...rails.specs]));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, reactionArrow, params);
  },
};

registerRecipe(reactionArrow);
